<template>
    <Rows-container 
        ref="rowsContainer" 
        class="details-lines"
        :items-props="partLines"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                :is-loading-next-page="item.isLoadingNextPage"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
                @load-next-page="loadNextPage"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick } from "vue"
    import { useI18n } from "vue-i18n";
    import { Strings } from "@/locales/types/enums";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesDetailShowStore } from "@/stores/LinesDetailShow";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { ExposeInterface } from "./types";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import { useDetailShowPageStore } from "@/stores/DetailShowPage";

    const DetailShowPage = useDetailShowPageStore();
    const {
        getIsLoadingEpisodes: isLoadingEpisodes,
    } = storeToRefs(DetailShowPage);
    const {
        fetchNextPageOfEpisodes
    } = DetailShowPage;

    

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const LinesDetailShowStore = useLinesDetailShowStore();
    const {
        getCounterLinesDetailShow,
        getCardsRelatedContent,
        getCardsCast,
        getCardsEpisodes
    } = storeToRefs(LinesDetailShowStore);

    const { increaseCounterLinesDetailShow } = LinesDetailShowStore;

    const emit = defineEmits(['show-all', 'list-item-selected', 'ready']);
    const { t } = useI18n();
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesDetailShow";
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.episodesCards,
                ref: shelfKeys.episodesCards,
                lineId: shelfKeys.episodesCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsEpisodes.value,
                isLoadingNextPage: isLoadingEpisodes.value,
            },
            {
                keyData: shelfKeys.castCards,
                title: t(Strings.DETAILS_CASTNCREW_TITLE).toString(),
                ref: shelfKeys.castCards,
                lineId: shelfKeys.castCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsCast.value,
            }, 
            {
                keyData: shelfKeys.relatedCards,
                title: t(Strings.DETAILS_RELATED_CONTENT_TITLE).toString(),
                ref: shelfKeys.relatedCards,
                lineId: shelfKeys.relatedCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsRelatedContent.value,
            },
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesDetailShow.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesDetailShow.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= AppConstants.HomeCounterLines) {
            const isIncreasing = (counter.value % AppConstants.HomeCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesDetailShow();
                counter.value++;
            }
        }
    };

    const loadNextPage = async (keyData: string): Promise<void> => {
        if( shelfKeys.episodesCards === keyData) {
            await fetchNextPageOfEpisodes();
        }
    };
    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesDetailShow.value < allLines.value.length) {
            increaseCounterLinesDetailShow();
        }
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>